import { LandingPageContent } from '../types';

/**
 * Language page for Arabic-speaking families in MENA and the diaspora. Like
 * the Greek page, it targets English-language queries ("arabic books for
 * kids") — the diaspora searches in English even when the product wanted is
 * in Arabic.
 */
const arabicBooksForKids: LandingPageContent = {
	path: '/personalized-arabic-books-for-kids',
	seoTitle: 'Personalized Arabic Books for Kids — Your Child as the Hero, in Arabic',
	metaDescription:
		'Personalized children’s books in Arabic: your child illustrated as the hero, with the story in Arabic and proper right-to-left layout. For families in MENA and the diaspora.',
	heading: 'Personalized Arabic Books for Kids',
	subheading: 'A story in Arabic where the hero is unmistakably yours.',
	heroImage: '/assets/hero/kid-1/classic_scene.png',
	breadcrumbParents: [{ name: 'Personalized story books', url: '/personalized-story-books' }],
	intro: `Arabic children's books exist in quantity. Arabic children's books a specific child will *choose* over the iPad — that is a shorter shelf. And a book in Arabic where your own child is the illustrated hero, with the layout actually built right-to-left rather than flipped as an afterthought: that is what we make.

You upload a photo, we illustrate your child into the story, and the book is produced in Arabic — reading direction, page order and binding all mirrored properly, because a right-to-left book that opens the wrong way convinces no one.

For diaspora families holding the line on Arabic at home, this is a tool as much as a gift.`,
	sections: [
		{
			heading: 'Why personalization matters more in a heritage language',
			body: `Every parent raising an Arabic-speaking child outside the Arab world knows the pattern: you speak Arabic to them, they answer in English. The environment outvotes you daily.

A book where the child is the hero changes the terms. The story is *about them*, and it only exists in Arabic — so the language stops being the obstacle and becomes the way in. No child needs persuading to care about a character who has their face.

It also hands the Arabic-speaking adults the best role. The book becomes the thing that Teta or Jiddo reads best — a small, renewable ritual that keeps the language attached to warmth rather than homework.`,
		},
		{
			heading: 'Right-to-left, done properly',
			body: `An Arabic book is not an English book with translated text. The spine is on the right, the pages turn left-to-right, the illustrations compose toward the reading direction.

We build the RTL edition as its own layout — mirrored page order, mirrored binding — rather than pasting Arabic into a left-to-right template. It sounds like a detail; open one wrong-way Arabic book with a child who attends Arabic school and you will learn it is not.

The name is handled with the same care: rendered in Arabic script within an Arabic story, so حبيبة is the hero in her own alphabet.`,
		},
		{
			heading: 'Who orders these',
			body: `**Families across MENA** — the Gulf, Egypt, the Levant — who want a personalized book that isn't English-by-default.

**Diaspora parents** in Europe and North America, playing defence for Arabic against an English-speaking world.

**Grandparents and relatives** gifting across borders. A book in Arabic, starring the grandchild abroad, is the rare long-distance gift that carries the culture with it.

**Bilingual households**, who often order the same story twice — Arabic and English — and let the child discover both versions are about them.`,
		},
		{
			heading: 'Occasions',
			body: `- **Eid.** The natural moment for a meaningful gift — a book lands differently from the usual eidiyah cash. See our [Eid gifts for kids guide](/gifts/eid-gifts-for-kids).
- **Ramadan.** A bedtime book in Arabic fits the month's rhythm of family evenings.
- **Birthdays**, where a personalized Arabic book stands out precisely because nobody else at the party brought one.
- **Milestones abroad** — starting Arabic school, a first trip back to the grandparents' country, or simply the ongoing project of making sure the language survives the move.`,
		},
	],
	picks: [
		{
			label: 'Personalized storybooks',
			href: '/personalized-story-books',
			description: 'The full storybook range — choose Arabic as the story language.',
		},
		{
			label: 'Personalized coloring books',
			href: '/personalized-coloring-books',
			description: 'Wordless fun that pairs well with an Arabic storybook.',
		},
	],
	faqs: [
		{
			question: 'Can I get a personalized children’s book in Arabic?',
			answer:
				'Yes. The story is produced in Arabic with a proper right-to-left layout — mirrored page order and binding, not a flipped English template — and your child appears as the illustrated hero throughout. You approve every page before printing.',
		},
		{
			question: 'Is the book genuinely right-to-left?',
			answer:
				'Yes. The RTL edition is built as its own layout: spine on the right, pages turning in the correct direction, illustrations composed for right-to-left reading.',
		},
		{
			question: 'Are personalized Arabic books good for children learning Arabic abroad?',
			answer:
				'They are one of the more effective motivators available. A story starring the child gives them a personal stake in the language, and it gives Arabic-speaking relatives a natural reading ritual with the child.',
		},
		{
			question: 'Do you ship to the Middle East and the Gulf?',
			answer:
				'Yes. Books are printed and shipped internationally, including across MENA, Europe and North America.',
		},
	],
	related: [
		{ label: 'Personalized story books', href: '/personalized-story-books' },
		{ label: 'Eid gifts for kids', href: '/gifts/eid-gifts-for-kids' },
		{ label: 'Personalized Greek books for kids', href: '/personalized-greek-books-for-kids' },
		{ label: 'Why personalized books help kids read', href: '/blog/why-personalized-books-help-kids-read' },
	],
	cta: { label: 'Make a book in Arabic', href: '/products' },
};

export default arabicBooksForKids;
